"use client";
// Interactive form (useState + a submit handler), so it must declare its own client
// boundary. It never had one because nothing rendered it -- its only importer was an
// unrendered sidebar component. The server-rendered article page now imports it
// directly, which would fail at runtime without this.
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { useNewsletterSubscribe } from '@/hooks/useNewsletterSubscribe';

interface NewsletterSubscriptionProps {
  title?: string;
  description?: string;
  buttonText?: string;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
  /**
   * Lead source attributed to a signup here. A prop rather than a constant: this
   * component takes title/description/variant props precisely so it can be reused
   * outside the blog, and a hardcoded source would silently mislabel every signup
   * from anywhere else.
   */
  source?: string;
}

export function NewsletterSubscription({
  title = "Stay Updated",
  description = "Get the latest HR insights and industry updates delivered to your inbox.",
  buttonText = "Subscribe",
  className = "",
  variant = 'default',
  source = 'blog',
}: NewsletterSubscriptionProps) {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { subscribe, isLoading } = useNewsletterSubscribe({
    source,
    onSuccess: () => {
      setIsSubscribed(true);
      setTimeout(() => {
        setEmail('');
        setIsSubscribed(false);
      }, 3000);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await subscribe(email);
  };

  if (variant === 'inline') {
    return (
      <div className={`flex gap-2 ${className}`}>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading || isSubscribed}
          className="flex-1"
        />
        <Button
          type="submit"
          disabled={isLoading || isSubscribed}
          onClick={handleSubmit}
          size="sm"
        >
          {isLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : isSubscribed ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm">{title}</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{description}</p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading || isSubscribed}
                className="text-sm"
              />
              <Button
                type="submit"
                disabled={isLoading || isSubscribed}
                size="sm"
              >
                {isLoading ? (
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : isSubscribed ? (
                  <CheckCircle className="h-3 w-3" />
                ) : (
                  <Send className="h-3 w-3" />
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading || isSubscribed}
          />
          <Button
            type="submit"
            disabled={isLoading || isSubscribed}
            className="w-full"
          >
            {isLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Subscribing...
              </>
            ) : isSubscribed ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Subscribed!
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {buttonText}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}